import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const colors = {
  coral: '#E8847C',
  coralLight: '#F5AEA8',
  mint: '#7ECEC1',
  mintLight: '#b8e4dd',
  cream: '#FFF9F5',
  warmGray: '#4A4543',
};

const pullQuotes = [
  '「いつか、本当に生きたものとして使いたい」',
  '「私が出逢えていない支援を必要としている女性の少しでも役に立ちたい…そんな気持ちが溢れてきました。」',
  '「人はお金があるから寄付や支援をするのではなく、自分自身も誰かに救われたり、誰かの笑顔に励まされたり、苦難や後悔を経験したり…そうした人とのつながりから生まれた価値観や使命感なのだと思うようになりました。」',
  '「“愛と希望の翼で世界を変える”\n父もそんな気持ちで羽を大切にしていたのかもしれないと思うと、何だか嬉しくなりました。」',
];

export default function DonorStory() {
  return (
    <div
      className="min-h-screen bg-white overflow-x-hidden"
      style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}
    >
      <Header variant="event" />

      <main className="pt-24">
        <section className="relative py-14 sm:py-20 overflow-hidden" style={{ backgroundColor: colors.cream }}>
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full opacity-30"
              style={{ background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 70%)`, filter: 'blur(90px)' }}
            />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-sm font-bold mb-8 transition-opacity hover:opacity-70"
              style={{ color: colors.mint }}
            >
              <i className="ri-arrow-left-line" aria-hidden />
              お知らせ一覧へ
            </Link>
            <span
              className="inline-block px-4 py-1.5 text-xs font-bold tracking-[0.18em] uppercase mb-5"
              style={{ backgroundColor: `${colors.coralLight}40`, color: colors.coral, borderRadius: '100px' }}
            >
              Donor Story
            </span>
            <p className="text-sm font-bold mb-3" style={{ color: colors.mint }}>
              寄付をくれた方のストーリー紹介
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-black leading-snug tracking-tight mb-4"
              style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
            >
              あたため続けた100万円が、<br className="hidden sm:block" />
              未来への希望になった日
            </h1>
            <p
              className="text-lg sm:text-xl font-bold mb-6"
              style={{ color: colors.coral, fontFamily: "'Shippori Mincho', serif" }}
            >
              ― 受け継がれた想いが、次の誰かへ羽ばたく ―
            </p>
            <p className="text-sm text-gray-500">2026年5月20日</p>
          </div>
        </section>

        <section className="py-10 sm:py-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-14">
              <figure className="rounded-2xl overflow-hidden shadow-lg bg-gray-50">
                <img
                  src="/images/donor-story-ceremony.png"
                  alt="感謝のセレモニーの様子（対馬ルリ子先生と歯科医師の先生）"
                  className="w-full h-auto object-cover"
                />
                <figcaption className="px-4 py-3 text-xs text-gray-500 bg-white">
                  2026年5月20日　感謝のセレモニー
                </figcaption>
              </figure>
              <figure className="rounded-2xl overflow-hidden shadow-lg bg-gray-50">
                <img
                  src="/images/donor-story-bouquet.png"
                  alt="感謝の花束をお持ちの歯科医師の先生"
                  className="w-full h-auto object-cover"
                />
                <figcaption className="px-4 py-3 text-xs text-gray-500 bg-white">
                  心を込めてお贈りいただいた花束
                </figcaption>
              </figure>
            </div>

            <article className="max-w-3xl mx-auto space-y-6 text-[15px] sm:text-base text-gray-600 leading-relaxed">
              <p className="text-lg text-gray-700 leading-relaxed">
                2026年5月20日、国際女性デーチャリティイベントを通じてご縁をいただいた歯科医師の先生へ、感謝の気持ちを込めたセレモニーを執り行いました。
              </p>
              <p>
                この日、私たちが受け取ったのは、単なるご寄付ではありませんでした。それは、長い年月をかけて育まれてきた「想い」そのものでした。
              </p>
              <p>
                先生が大学受験に合格された時、お祝いとして叔父様から贈られた100万円。その大切なお金を、先生は長い間手元に置き続けてこられたそうです。
              </p>

              <blockquote
                className="py-6 px-6 sm:px-8 rounded-2xl border-l-4 text-base sm:text-lg font-bold leading-relaxed whitespace-pre-line"
                style={{
                  borderColor: colors.coral,
                  backgroundColor: `${colors.cream}`,
                  color: colors.warmGray,
                  fontFamily: "'Shippori Mincho', serif",
                }}
              >
                {pullQuotes[0]}
                <span className="block mt-3 text-sm font-normal text-gray-600">
                  その想いを胸に、静かに大切にあたため続けてこられました。
                </span>
              </blockquote>

              <p>
                そして今回、国際女性デーチャリティイベントへの参加が、その想いを動かす大きなきっかけとなりました。イベント公式サイトに掲載された「このイベントが届けたいこと」の言葉の数々。そして、トークセッションの最後に、対馬ルリ子先生が言葉を詰まらせながらも、女性の健康や未来への願いを語られた姿が深く心に残ったそうです。
              </p>
              <p>先生はこう話してくださいました。</p>
              <p>
                「女性のヘルスケアの重要性について、一貫して真剣に誠実に発信し続ける姿勢に、同じ医療に携わる者として胸を打たれました。」
              </p>

              <blockquote
                className="py-6 px-6 sm:px-8 rounded-2xl text-center"
                style={{ backgroundColor: `${colors.mintLight}50` }}
              >
                <p className="text-base sm:text-lg font-bold leading-relaxed whitespace-pre-line" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                  {pullQuotes[1]}
                </p>
              </blockquote>

              <p>
                大学進学という人生の節目で受け取った贈り物を、今度は誰かの未来へつなげたい。そうして、このたび100万円のご寄付をお寄せくださいました。
              </p>
              <p>
                しかし、お話を伺う中で気づかされたことがあります。今回のご寄付の背景には、もっと前から先生の心の中で育まれてきたものがありました。
              </p>
              <p>
                先生のお父様は歯科医師として地域医療に携わる傍ら、献血活動や赤十字募金、ユニセフ募金、赤い羽根募金などに積極的に取り組まれていたそうです。毎日、背広で仕事へ向かうお父様の胸元には、必ず赤い羽根がついていました。
              </p>
              <p>
                子どもの頃の先生は、その赤い羽根が何を意味するのか知りませんでした。けれど成長する中で、それが地域福祉を応援する意思表示であることを知った時、お父様の姿が違って見えたといいます。
              </p>

              <blockquote
                className="py-6 px-6 sm:px-8 rounded-2xl border-l-4 text-base sm:text-lg leading-relaxed whitespace-pre-line"
                style={{
                  borderColor: colors.mint,
                  backgroundColor: 'white',
                  boxShadow: '0 4px 24px rgba(74,69,67,0.06)',
                  color: colors.warmGray,
                }}
              >
                {pullQuotes[2]}
              </blockquote>

              <p>
                人から受け取った想いは、自分の中で育ち、やがて誰かへの優しさへ変わっていく。先生から教えていただいたのは、お金の話ではなく、&ldquo;想いは受け継がれていく&rdquo;ということでした。
              </p>
              <p>
                また、対馬先生から聞いた『スイミー』の物語も心に残っていたそうです。小さな力でも、一つひとつが集まれば大きな力になる。
              </p>

              <blockquote
                className="py-8 px-6 sm:px-10 rounded-2xl text-center"
                style={{
                  background: `linear-gradient(135deg, ${colors.coralLight}30 0%, ${colors.mintLight}40 100%)`,
                }}
              >
                <p
                  className="text-lg sm:text-xl font-black leading-relaxed whitespace-pre-line"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  {pullQuotes[3]}
                </p>
              </blockquote>

              <p>
                赤い羽根から始まった想いが、時を越え、新しい未来へ羽ばたいていく。私たちは今回、その美しい循環を目の前で見せていただいたような気がしています。
              </p>
              <p className="text-lg font-bold pt-4" style={{ color: colors.warmGray }}>
                この尊い想いを、女性たちの健康支援、そしてWell-being推進活動として責任を持って未来へつないでまいります。
              </p>
              <p className="text-center font-bold pt-6" style={{ color: colors.coral }}>
                心より感謝申し上げます。
              </p>
            </article>

            <div className="max-w-3xl mx-auto mt-14 pt-10 border-t border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-sm font-bold transition-opacity hover:opacity-70"
                style={{ color: colors.mint }}
              >
                <i className="ri-arrow-left-line" aria-hidden />
                お知らせ一覧へ
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 transition-opacity hover:opacity-70"
              >
                トップページへ
                <i className="ri-arrow-right-line" aria-hidden />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
